import {
  createContext,
  useCallback,
  useRef,
  type FC,
  type ReactNode,
} from "react";
import type { Channel, PresenceChannel } from "pusher-js";

import type { ChannelsContextValues } from "./types.js";
import { usePusher } from "./usePusher.js";

// context setup
const ChannelsContext = createContext<ChannelsContextValues>({});
export const __ChannelsContext = ChannelsContext;

type AcceptedChannels = Channel | PresenceChannel;
type ConnectedChannels = {
  [channelName: string]: AcceptedChannels[];
};

/**
 * Provider that creates your channels instances and provides it to child hooks throughout your app.
 */

export const ChannelsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { client, channelPrefix } = usePusher();
  const connectedChannels = useRef<ConnectedChannels>({});

  // Subscriptions must carry the same prefix the server-side publisher
  // applies to channel names (namespacing concurrent environments that share
  // one Pusher app), or its events are never received.
  const prefixed = useCallback(
    (channelName: string) =>
      [channelPrefix, channelName].filter(Boolean).join("-"),
    [channelPrefix],
  );

  const subscribe = useCallback(
    <T extends Channel & PresenceChannel>(channelName: string) => {
      /** Return early if there's no client */
      if (!client || !channelName) return;

      /** Subscribe to channel and set it in state */
      const prefixedName = prefixed(channelName);
      const pusherChannel = client.subscribe(prefixedName);
      connectedChannels.current[prefixedName] = [
        ...(connectedChannels.current[prefixedName] || []),
        pusherChannel,
      ];
      return pusherChannel as T;
    },
    [client, connectedChannels, prefixed],
  );

  const unsubscribe = useCallback(
    (channelName: string) => {
      const prefixedName = prefixed(channelName);
      const channels = connectedChannels.current[prefixedName];
      /** Return early if there's no props */
      if (!client || !channelName || channels === undefined) return;
      /** If just one connection, unsubscribe totally*/
      if (channels.length === 1) {
        client.unsubscribe(prefixedName);
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete connectedChannels.current[prefixedName];
      } else {
        channels.pop();
      }
    },
    [connectedChannels, client, prefixed],
  );

  const getChannel = useCallback(
    <T extends Channel & PresenceChannel>(channelName: string) => {
      const prefixedName = prefixed(channelName);
      const channel = connectedChannels.current[prefixedName]?.[0];
      /** Return early if there's no client */
      if (!client || !channelName || channel === undefined) return;
      /** Return channel */
      return channel as T;
    },
    [connectedChannels, client, prefixed],
  );

  return (
    <ChannelsContext.Provider
      value={{
        unsubscribe,
        subscribe,
        getChannel,
      }}
    >
      {children}
    </ChannelsContext.Provider>
  );
};
